import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { SourceStatusProps } from '@databox/ember-integrations/lib/components/Datasets/SourceStatus/SourceStatus';
import SourceStatusEntryPoint from '@databox/ember-integrations/lib/components/Datasets/SourceStatus/SourceStatusEntryPoint';

import type { ReactIntegrations, Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { getShadowDomMode } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

import type Source from 'webapp/models-custom/source';
import { mapSourceToDataSource, mapSyncInfo } from 'webapp/models/helpers/react-model-mapper';
import type { DataSourceSyncInfo } from 'webapp/models/source';

interface Args {
	dataSource: Source;
}

export default class SourceStatusComponent extends Component<Args> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('account') declare accountService: Service.Account;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('dataset-events') declare datasetEventsService: Service.DatasetEvents;
	@service('billing') declare billingService: Service.Billing;
	@service('react') declare reactService: Service.React;

	node: HTMLDivElement | null = null;
	reactElement: ReactIntegrations.Root = null;

	get lastSync(): DataSourceSyncInfo | undefined {
		return this.args.dataSource?.lastSync;
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node as HTMLDivElement;
		this.reactElement = this.reactService.create(this.node);

		this.renderStatus();

		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.renderStatus);
		this.eventManagerService.on(AppEvent.DATAMANAGER_SOURCES_CONTENT_CHANGED, this, () => {
			this.reactService.unmount(this.reactElement);
			this.reactElement = this.reactService.create(node as HTMLDivElement);
			this.renderStatus();
		});
	}

	renderStatus() {
		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		const props: SourceStatusProps = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			accessToken: accessToken!,
			dataToken: dataToken!,
			spaceId: this.accountService.id,
			dataSource: {
				...mapSourceToDataSource(this.args.dataSource, this.currentUserService.numericId),
				syncInfo: mapSyncInfo(this.args.dataSource.lastSync),
			},
			isLoading: false,
			isWhiteLabeled: this.accountService.whiteLabelingEnabled,
			onDataRefresh: this.datasetEventsService.handleDataRefresh,
			dataSourceTypeKey: this.args.dataSource.sourceType.key,
			getInvalidatedBillingInfoQuery: (invalidatedBillingInfoQuery) => {
				this.billingService.invalidateFeatureCache = invalidatedBillingInfoQuery;
			},
		};

		if (this.reactElement) {
			this.reactService.render(
				this.reactElement,
				SourceStatusEntryPoint(props),
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	@action
	teardownView() {
		this.reactService.unmount(this.reactElement);
		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
		this.eventManagerService.off(AppEvent.DATAMANAGER_SOURCES_CONTENT_CHANGED, this);
	}

	@action
	updateReactComponent(): void {
		this.renderStatus();
	}
	<template>
		<div
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
			{{didUpdate this.updateReactComponent this.lastSync}}
		></div>
	</template>
}
